import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Box, Stack } from '@mui/material';

import IdeaCard from '../../cards/IdeaCard';

import type { BoardLayout } from '../../../interfaces/BoardDetails';
import type { Idea } from '../../../interfaces/Idea';
import { moveCardOrderItem } from '../../../utils/boardCardOrdering';

const areCardOrdersEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((cardId, index) => cardId === right[index]);

type CustomBoardCardsProps = {
  orderedIdeas: Idea[];
  layout: BoardLayout;
  laneCount: number;
  dragEnabled: boolean;
  onReorder: (cardOrder: string[]) => Promise<void>;
};

const CustomBoardCards = ({
  orderedIdeas,
  layout,
  laneCount,
  dragEnabled,
  onReorder,
}: CustomBoardCardsProps) => {
  const [draggingIdeaId, setDraggingIdeaId] = useState<string | null>(null);
  const [dragCardOrder, setDragCardOrder] = useState<string[]>([]);
  const dragStartCardOrderRef = useRef<string[]>([]);
  const dragCardOrderRef = useRef<string[]>([]);
  const draggingIdeaIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dragEnabled || draggingIdeaId) {
      return;
    }

    const nextCardOrder = orderedIdeas.map((idea) => idea.id);
    dragCardOrderRef.current = nextCardOrder;
    setDragCardOrder(nextCardOrder);
  }, [dragEnabled, draggingIdeaId, orderedIdeas]);

  const dragOrderedIdeas = useMemo(() => {
    if (!dragEnabled || dragCardOrder.length === 0) {
      return orderedIdeas;
    }

    const visibleIdeasById = new Map(orderedIdeas.map((idea) => [idea.id, idea]));
    const sortedVisibleIdeas = dragCardOrder
      .map((ideaId) => visibleIdeasById.get(ideaId))
      .filter((idea): idea is Idea => Boolean(idea));

    if (sortedVisibleIdeas.length === 0) {
      return orderedIdeas;
    }

    const sortedIdeaIds = new Set(sortedVisibleIdeas.map((idea) => idea.id));
    const missingIdeas = orderedIdeas.filter((idea) => !sortedIdeaIds.has(idea.id));

    return [...sortedVisibleIdeas, ...missingIdeas];
  }, [dragCardOrder, dragEnabled, orderedIdeas]);

  const laneColumns = useMemo(() => {
    const columns = Array.from({ length: laneCount }, () => [] as Idea[]);

    dragOrderedIdeas.forEach((idea, index) => {
      columns[index % laneCount].push(idea);
    });

    return columns;
  }, [dragOrderedIdeas, laneCount]);

  const handleIdeaDragStart = (event: DragEvent<HTMLElement>, ideaId: string) => {
    if (!dragEnabled) {
      return;
    }

    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ideaId);

    const baseCardOrder = dragOrderedIdeas.map((idea) => idea.id);
    dragStartCardOrderRef.current = baseCardOrder;
    dragCardOrderRef.current = baseCardOrder;
    draggingIdeaIdRef.current = ideaId;
    setDragCardOrder(baseCardOrder);
    setDraggingIdeaId(ideaId);

    const dragElement = event.currentTarget as HTMLElement;
    const dragElementRect = dragElement.getBoundingClientRect();
    const dragPreview = dragElement.cloneNode(true) as HTMLElement;
    dragPreview.style.position = 'fixed';
    dragPreview.style.top = '-1000px';
    dragPreview.style.left = '-1000px';
    dragPreview.style.width = `${dragElementRect.width}px`;
    dragPreview.style.opacity = '0.95';
    dragPreview.style.transform = 'rotate(1deg)';
    dragPreview.style.pointerEvents = 'none';
    dragPreview.style.zIndex = '2147483647';
    document.body.appendChild(dragPreview);

    event.dataTransfer.setDragImage(
      dragPreview,
      event.clientX - dragElementRect.left,
      event.clientY - dragElementRect.top
    );
    window.requestAnimationFrame(() => {
      document.body.removeChild(dragPreview);
    });
  };

  const handleIdeaDragOver = (event: DragEvent<HTMLElement>, targetIdeaId: string) => {
    if (!dragEnabled || !draggingIdeaId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    setDragCardOrder((currentCardOrder) => {
      const baseCardOrder =
        currentCardOrder.length > 0 ? currentCardOrder : dragOrderedIdeas.map((idea) => idea.id);
      const nextCardOrder = moveCardOrderItem(baseCardOrder, draggingIdeaId, targetIdeaId);
      dragCardOrderRef.current = nextCardOrder;
      return nextCardOrder;
    });
  };

  const handleIdeaDrop = (event: DragEvent<HTMLElement>) => {
    if (!dragEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const finalizeIdeaDrag = () => {
    if (!dragEnabled || !draggingIdeaIdRef.current) {
      return;
    }

    const currentCardOrder =
      dragCardOrderRef.current.length > 0
        ? dragCardOrderRef.current
        : dragOrderedIdeas.map((idea) => idea.id);
    const startCardOrder = dragStartCardOrderRef.current;

    setDraggingIdeaId(null);
    dragStartCardOrderRef.current = [];
    draggingIdeaIdRef.current = null;

    if (!areCardOrdersEqual(currentCardOrder, startCardOrder)) {
      void onReorder(currentCardOrder);
    }
  };

  const getIdeaDragHandleProps = (ideaId: string) => {
    if (!dragEnabled) {
      return {};
    }

    return {
      draggable: true,
      onDragStart: (event: DragEvent<HTMLElement>) => handleIdeaDragStart(event, ideaId),
      onDragOver: (event: DragEvent<HTMLElement>) => handleIdeaDragOver(event, ideaId),
      onDrop: handleIdeaDrop,
      onDragEnd: finalizeIdeaDrag,
    };
  };

  if (layout === 'grid') {
    return (
      <Box onDropCapture={finalizeIdeaDrag} onDragEndCapture={finalizeIdeaDrag}>
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            alignItems: 'start',
            gap: 2,
          }}
        >
          {dragOrderedIdeas.map((idea) => (
            <Box
              key={idea.id}
              sx={{
                opacity: draggingIdeaId === idea.id ? 0.45 : 1,
                cursor: dragEnabled ? 'grab' : 'default',
                '&:active': {
                  cursor: dragEnabled ? 'grabbing' : 'default',
                },
              }}
              {...getIdeaDragHandleProps(idea.id)}
            >
              <IdeaCard ideaId={idea.id} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box onDropCapture={finalizeIdeaDrag} onDragEndCapture={finalizeIdeaDrag}>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${laneCount}, minmax(0, 1fr))`,
          alignItems: 'start',
          gap: 2,
        }}
      >
        {laneColumns.map((columnIdeas, columnIndex) => (
          <Stack key={`lane-column-${columnIndex}`} spacing={2}>
            {columnIdeas.map((idea) => (
              <Box
                key={idea.id}
                sx={{
                  opacity: draggingIdeaId === idea.id ? 0.45 : 1,
                  cursor: dragEnabled ? 'grab' : 'default',
                  '&:active': {
                    cursor: dragEnabled ? 'grabbing' : 'default',
                  },
                }}
                {...getIdeaDragHandleProps(idea.id)}
              >
                <IdeaCard ideaId={idea.id} />
              </Box>
            ))}
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default CustomBoardCards;
